/**
 * Axios Instance Configuration
 * Configured axios instance with interceptors for authentication and error handling
 *
 * Authentication is handled via Authorization header with session_id in JSON format.
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_CONFIG } from '../config/api.config';
import { formatApiError, showErrorMessage, getStatusMessage } from '../utils/errorHandler';

/**
 * Create axios instance with base configuration
 */
console.log('🔧 Axios Base URL:', API_CONFIG.BASE_URL);
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  transformResponse: [
    (data, headers) => {
      // If data is already an object, return it
      if (typeof data === 'object') {
        return data;
      }

      // Try to parse JSON
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch (e) {
          console.error('❌ JSON Parse Error:', {
            error: e,
            data: data.substring(0, 200),
            contentType: headers?.['content-type']
          });
          // Return error object instead of throwing
          return {
            error: 'Invalid JSON response',
            rawData: data.substring(0, 500),
            parseError: e instanceof Error ? e.message : String(e)
          };
        }
      }

      return data;
    }
  ]
});

/**
 * Request Interceptor
 * Adds Authorization header with session_id to all authenticated requests.
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get session_id from localStorage
    const sessionId = localStorage.getItem('session_id');

    // Add Authorization header if session_id exists
    if (sessionId) {
      config.headers.Authorization = JSON.stringify({ session_id: sessionId });
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 API Request:', {
        url: config.url,
        fullUrl: config.baseURL ? config.baseURL + config.url : config.url,
        method: config.method?.toUpperCase(),
        hasSessionId: !!sessionId,
        sessionIdPreview: sessionId ? sessionId.substring(0, 8) + '...' : 'none',
        headers: config.headers,
        data: config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    // Handle request error
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Request Error:', error);
    }
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles responses and errors globally
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', {
        url: response.config.url,
        status: response.status,
        contentType: response.headers['content-type'],
        data: response.data,
      });
    }

    // Validate JSON response
    const contentType = response.headers['content-type'];
    if (contentType && !contentType.includes('application/json')) {
      console.warn('⚠️ Non-JSON response detected:', {
        url: response.config.url,
        contentType,
        data: typeof response.data === 'string' ? response.data.substring(0, 200) : response.data
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.message,
        data: error.response?.data,
      });
    }

    // Handle different error scenarios
    if (error.response) {
      const status = error.response.status;

      switch (status) {
        case 401:
          // Unauthorized - Clear session and user data, redirect to login
          localStorage.removeItem('session_id');
          localStorage.removeItem('user');

          // Only show error if not already on login page
          if (!window.location.pathname.includes('/login')) {
            showErrorMessage(error, 'Session expired. Please login again.');

            // Redirect to login after a short delay
            setTimeout(() => {
              window.location.href = '/login';
            }, 1500);
          }
          break;

        case 403:
          // Forbidden
          showErrorMessage(error, getStatusMessage(403));
          break;

        case 404:
          // Not Found
          showErrorMessage(error, 'Resource not found.');
          break;

        case 422:
          // Validation Error - don't show generic message, let component handle it
          break;

        case 429:
          // Too Many Requests
          showErrorMessage(error, getStatusMessage(429));
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server Errors
          showErrorMessage(error, 'Server error. Please try again later.');
          break;

        default:
          // Other errors - show error message from response if available
          if (!error.config?.url?.includes('/auth/')) {
            showErrorMessage(error);
          }
      }
    } else if (error.code === 'ECONNABORTED') {
      // Timeout error
      showErrorMessage(error, 'Request timeout. Please try again.');
    } else if (error.message === 'Network Error') {
      // Network error
      showErrorMessage(error, 'Network error. Please check your connection.');
    } else {
      // Other errors
      showErrorMessage(error);
    }

    // Return formatted error
    return Promise.reject(formatApiError(error));
  }
);

/**
 * Helper function to clear auth data
 * Clears both session_id and user data from localStorage.
 */
export const clearAuthData = (): void => {
  localStorage.removeItem('session_id');
  localStorage.removeItem('user');
};

export default axiosInstance;
